import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Crown, Lock, Sparkles, Check, Gift } from 'lucide-react';

const PERKS = [
  'Full litigation library: every complaint, order PDF and AI class brief',
  'AI finance brief: Cost of Project vs Means of Finance, escrow & debt pressure',
  'Quarterly Form 1/2/3 filings with extracted figures and original PDFs',
  'Project Archive: construction, sanctions & promoter records with AI conclusions',
  'New-order email alerts and every future case study we add',
];

export const goSubscribe = () => window.dispatchEvent(new CustomEvent('fbc-nav', { detail: 'subscription' }));

const price = (acc) => (acc?.founding ? acc?.prices?.['library-founding'] : acc?.prices?.['library-annual']) || 1499;

export const LibraryBanner = ({ acc, onSample }) => {
  if (!acc || acc.reason === 'admin' || acc.reason === 'subscriber') return null;
  if (acc.reason === 'trial') {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2" data-testid="library-trial-banner">
        <Gift className="h-4 w-4 text-amber-700" />
        <p className="text-xs flex-1 min-w-[200px]"><b>Founding-member free access</b> — {acc.days_left} day{acc.days_left === 1 ? '' : 's'} left. Lock in the founding price of <b>₹{price(acc)}/year</b> before it ends.</p>
        <Button size="sm" className="h-8 bg-amber-600 hover:bg-amber-700 text-white" onClick={goSubscribe} data-testid="library-trial-subscribe"><Crown className="h-3.5 w-3.5 mr-1" /> Subscribe</Button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg bg-slate-900 text-slate-50 px-4 py-3" data-testid="library-tour-banner">
      <Lock className="h-4 w-4 text-emerald-400" />
      <p className="text-xs flex-1 min-w-[220px] leading-relaxed">
        {acc.reason === 'trial_ended'
          ? <>Your founding-member free access has ended. Continue at the founding price of <b>₹{price(acc)}/year</b>.</>
          : <><b>Free tour</b> — you can open one sample case and one sample class brief. Unlock <b>FBC Project Intelligence</b> for <b>₹{price(acc)}/year</b>: the full library, finance briefs, filings and archive.</>}
      </p>
      <Button size="sm" variant="outline" className="h-8 bg-transparent border-slate-600 text-slate-50 hover:bg-slate-800 hover:text-white" onClick={onSample} data-testid="library-sample-brief"><Sparkles className="h-3.5 w-3.5 mr-1 text-emerald-400" /> Sample brief</Button>
      <Button size="sm" className="h-8 bg-emerald-500 hover:bg-emerald-400 text-slate-950" onClick={goSubscribe} data-testid="library-tour-subscribe"><Crown className="h-3.5 w-3.5 mr-1" /> Subscribe</Button>
    </div>
  );
};

export const LockedCasesCard = ({ n, acc }) => (n > 0 ? (
  <div className="rounded-lg border-2 border-dashed p-5 text-center" data-testid="library-locked-cases">
    <Lock className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
    <p className="text-sm font-medium">Names and AI summaries for {n} case{n === 1 ? '' : 's'} in this view are in the full library</p>
    <p className="text-xs text-muted-foreground mt-1 mb-3">The register, status, outcome and original orders above are free. Parties' names, AI gists, case journeys in full and the class brief — ₹{price(acc)}/year.</p>
    <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={goSubscribe} data-testid="library-locked-subscribe"><Crown className="h-3.5 w-3.5 mr-1" /> Unlock the library</Button>
  </div>
) : null);

export default function PaywallDialog({ open, acc, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="library-paywall">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Crown className="h-5 w-5 text-amber-600" /> Unlock FBC Project Intelligence</DialogTitle>
          <DialogDescription>One pass for everything FBC compiles on your project: litigation, finances, filings and the archive.</DialogDescription>
        </DialogHeader>
        <ul className="space-y-2">{PERKS.map((p) => <li key={p} className="text-sm flex gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />{p}</li>)}</ul>
        <div className="rounded-lg bg-muted/60 p-3 text-center">
          <p className="text-3xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="library-paywall-price">₹{price(acc)}<span className="text-sm font-normal text-muted-foreground">/year</span></p>
          {acc?.founding && <p className="text-xs text-amber-700 mt-1">Founding-member price (regular ₹{acc?.prices?.['library-annual'] || 1499})</p>}
          <p className="text-[11px] text-muted-foreground mt-1">Less than a single consultation with a lawyer.</p>
        </div>
        <Button className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => { onClose(); goSubscribe(); }} data-testid="library-paywall-subscribe">Subscribe now</Button>
      </DialogContent>
    </Dialog>
  );
}
